<script lang="ts">
import {defineComponent, watch} from 'vue';
import {ChartDatafeed} from '../../utils/ChartDatafeed';
import {store} from '../../store/Store';
import ButtonBar from './ButtonBar.vue';
import {AreaSeries, CandlestickSeries, createChart, IChartApi, ISeriesApi} from 'lightweight-charts';

const CHART_REFRESH_INTERVAL = 30 * 1000;

export default defineComponent({
  name: 'ChartComponent',
  computed: {
    frontToken(): string {
      return store.frontToken;
    },
    backToken(): string {
      return store.backToken;
    },
  },
  components: {
    ButtonBar,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    extraClass: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      chart: null as IChartApi | null,
      series: null as ISeriesApi<'Area'> | ISeriesApi<'Candlestick'> | null,
      chartType: 'area' as 'area' | 'candlestick',
      cardTarget: null as HTMLElement | null,
      cardIsVisible: false,
      chartData: [] as any[],
      datafeed: new ChartDatafeed(),
      observer: null as IntersectionObserver | null,
      isScrollingInProgress: false,
      chartRefreshTimer: null as ReturnType<typeof setInterval> | null,
      chartOptions: {
        layout: {
          textColor: 'black',
          background: { color: '#ffffff' }
        },
        grid: {
          vertLines: { visible: false },
          horzLines: { visible: false }
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          rightOffset: 10,
          fixRightEdge: true,
        },
        height: 420, // ✅ You can pass this to `createChart`, but it's not part of ChartOptions directly.
        localization: {
          priceFormatter: (price: number) => price.toFixed(6),
        },
        rightPriceScale: {
          visible: true,
        }
      },
      chartPriceFormatter: {
        priceFormat: {
          type: 'price',
          precision: 2,
          minMove: 0.000001
        }
      }
    };
  },
  methods: {
    resetChartState() {
      this.chart?.remove();
      this.chart = null;
      this.chartData = [];
      this.series = null;
    },
    formatChartData(data: any[]) {
      const seen = new Set<number>();
      return data
        .map((item) => ({
          time: Math.floor(item.DT / 1000),
          open: item.Open,
          high: item.High,
          low: item.Low,
          close: item.Close,
          value: item.Close,
        }))
        .filter((item) => {
          if (seen.has(item.time)) return false;
          seen.add(item.time);
          return true;
        })
        .sort((a, b) => a.time - b.time);
    },
    async loadChart(isRefresh = false) {
      if (!store.frontToken || !store.backToken) return;

      const container = document.getElementById(`chart-block-inner-${this.type}`);
      if (!container) return;

      this.chart = createChart(container, this.chartOptions);
      this.addSeries();
      await this.loadChartData(isRefresh);
      this.subscribeScrollLoading();

      // this.chart?.timeScale().setRightOffset(0);

      // this.chart?.timeScale().subscribeVisibleTimeRangeChange(() => {
      //   if (this.chart?.timeScale().getRightOffset() > 0) {
      //     this.chart?.timeScale().setRightOffset(0);
      //   }
      // });


    },
    addSeries() {
      if (!this.chart) return;

      this.series = this.chartType === 'candlestick'
        ? this.chart.addSeries(CandlestickSeries, this.chartPriceFormatter)
        : this.chart.addSeries(AreaSeries, this.chartPriceFormatter);
    },
    async refreshLatestChartData() {
      try {
        // Load fresh data (without resetting the chart)
        await this.datafeed.loadLatestChartData(50, store.chartInterval, false);
        const latestChartData = this.datafeed.getLatestChartData();
        if (!latestChartData.length) return;
        // Use the latest (last) data point
        const item = latestChartData[latestChartData.length - 1];
        this.series.update({
          time: Math.floor(item.DT / 1000),
          open: item.Open,
          high: item.High,
          low: item.Low,
          close: item.Close,
          value: item.Close,
        });
        // Optional: also update chartData if you're tracking it
        this.chartData[this.chartData.length - 1] = item;

      } catch (error) {
        console.error('Error refreshing chart data:', error);
      }
    },

    async loadChartData(isRefresh = false) {
      await this.datafeed.loadChartDataAsync(50, store.chartInterval, isRefresh);
      const initialData = this.datafeed.getLastChartData();
      if (!initialData.length) return;
      this.chartData = initialData;
      this.setChartData();
    },
    subscribeScrollLoading() {
      this.chart?.timeScale().subscribeVisibleLogicalRangeChange(async (range) => {
        if (range && range.from < 10 && !this.isScrollingInProgress) {
          this.isScrollingInProgress = true;
          const barsToLoad = 50 - range.from;
          await this.datafeed.loadChartDataAsync(barsToLoad, store.chartInterval, false);
          const moreData = this.datafeed.getLastChartData();
          this.chartData = [...this.chartData, ...moreData];
          setTimeout(() => {
            this.setChartData();
          }, 500);
        }
      });
    },
    setChartData() {
      if (!this.series) return;
      const formattedData = this.formatChartData(this.chartData);
      this.series.setData(formattedData as any);
      this.isScrollingInProgress = false;
    },
    toggleChartType() {
      this.chartType = this.chartType === 'area' ? 'candlestick' : 'area';
      this.refreshChart(true);
    },
    refreshChart(isRefresh = false) {
      if (!store.frontToken || !store.backToken) return;
      this.resetChartState();
      this.loadChart(isRefresh);
    },
    invertPair() {
      this.resetChartState();
      store.invertPair();
    },
    intersection(entries: IntersectionObserverEntry[]) {
      const wasVisible = this.cardIsVisible;
      for (const entry of entries) {
        if (entry.target === this.cardTarget) {
          this.cardIsVisible = entry.isIntersecting;
        }
      }
      if (this.cardIsVisible !== wasVisible) this.refreshChart();
    },
  },
  mounted() {
    const selector = `#chart-block-inner-${this.type}`;
    this.cardTarget = document.querySelector(selector) as HTMLElement;

    if (this.cardTarget) {
      this.observer = new IntersectionObserver(this.intersection);
      this.observer.observe(this.cardTarget);
    }
    watch(
      () => store.chartInterval,
      () => {
        this.resetChartState();
        this.refreshChart(true);
      },
      { immediate: true }
    );
    watch(
      [() => store.frontToken, () => store.backToken],
      ([front, back], [prevFront, prevBack]) => {
        const hasChanged = front !== prevFront || back !== prevBack;
        if (hasChanged && front && back) {
          // alert(`Chart updated for ${front} / ${back}`);
          this.resetChartState();
          this.refreshChart(true);
        }
      },
      { immediate: true }
    );
    this.chartRefreshTimer = setInterval(() => {
      if (this.cardIsVisible) {
        this.refreshLatestChartData();
      }
    }, CHART_REFRESH_INTERVAL);
  },
  beforeUnmount() {
    if (this.observer && this.cardTarget) {
      this.observer.unobserve(this.cardTarget);
      this.observer.disconnect();
    }
    if (this.chartRefreshTimer) {
      clearInterval(this.chartRefreshTimer);
    }
  },
});
</script>
<template>
  <div :ref="`chart-container-block-${type}`" :id="`chart-container-block-${type}`"
    :class="['container-block', 'container-chart', extraClass]">
    <div class="chart-controls">
      <span class="alice-ticket-title-front">
        <img :src="`/crypto-icons/${frontToken.toLowerCase()}.svg`" class="alice-crypto-icon-small" />
        <img :src="`/crypto-icons/${backToken.toLowerCase()}.svg`"
          class="alice-crypto-icon-small alice-crypto-icon-right" />
        {{ frontToken }} /
      </span>
      <span class="alice-ticket-title-back">
        {{ backToken }}
        &nbsp;
        <o-icon id="alice-chart-invert-icon" pack="fas" icon="arrow-right-arrow-left" class="alice-ticket-invert-icon"
          @click="invertPair" />
      </span>
    </div>

    <div class="alice-chart-header-right">
      <o-icon pack="fas" icon="chart-simple" class="alice-chart-type-icon" @click="toggleChartType" />
      <ButtonBar />
    </div>
  </div>

  <div :ref="`chart-block-inner-${type}`" :id="`chart-block-inner-${type}`" class="chart-block-inner" />
</template>


<style scoped>
.o-chk__check {
  color: #6eaa51;
  background-color: #ffffff;
}

.chart-block-inner {
  margin-top: 3%;
  width: 100%;
  height: 90%;
  position: relative;
}

.alice-ticket-title {
  text-align: left;
}

.chart-controls {
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #1f1f1f;
}

.alice-ticket-invert-icon {
  transform: translate(0, 2px);
  color: var(--chart-invert-button-colour);
  cursor: pointer;
}

.alice-chart-type-icon {
  transform: translate(0, 2px);
  margin-right: 30px;
  color: var(--nav-outline-colour);
  cursor: pointer;
}

.alice-crypto-icon-small {
  height: 24px;
  transform: translate(0, 5px);
}

.alice-crypto-icon-right {
  transform: translate(-7px, 5px);
}

.alice-chart-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: -15px;
  margin-bottom: -5px;
}

.alice-chart-header-left {
  text-align: left;
}

.alice-chart-header-right {
  text-align: right;
}

.alice-chart-header-rate {
  color: #111111;
  display: inline-block;
  margin-top: 14px;
  font-size: 11px;
  font-weight: 400;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
}

.alice-chart-header-tokens {
  color: #000000;
  display: inline-block;
  padding-left: 10px;
  font-size: 14px;
  font-weight: 400;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
}

/* formats for narrow screens */
@media (max-width: 959px) {
  .chart-block-inner {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 50vh;
  }
}

@media (max-width: 420px) {
  .chart-block-inner {
    display: block;
    width: 100%;
    height: 80%;
    padding: 0px;
    margin: 0px;
    min-height: 50vh;
  }

  .alice-chart-header {
    margin-top: 0px;
    padding-top: 10px;
  }

  .alice-chart-header-right {
    margin-top: -10px;
  }

  .alice-chart-type-icon {
    display: none;
  }
}

@media (max-width: 330px) {
  .container-chart {
    height: 80%;
  }

  .chart-block-inner {
    min-height: 370px;
  }
}
</style>